import { useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import DeleteConfirmModal from '@/components/DeleteConfirmModal'
import { ArrowLeft, Save, Trash2, X, Award, DollarSign } from 'lucide-react'

interface EquityCredit {
  id: number
  agent_id: number
  agent_name: string
  credit_amount: number
  credit_type: string
  event_name: string
  event_date: string
  notes: string | null
  source_type: string | null
  source_id: number | null
  granted_by_id: number | null
  granted_by_name: string | null
  created_at: string
}

interface Agent {
  id: number
  name: string
}

interface Props extends PageProps {
  credit: EquityCredit
  agents: Agent[]
}

export default function EquityCreditShow({ credit, agents }: Props) {
  const { auth } = usePage<PageProps>().props
  const isAdmin = auth.user?.role === 'owner' || auth.user?.role === 'administrator'
  const canDelete = isAdmin
  
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [form, setForm] = useState({
    agent_id: credit.agent_id,
    credit_amount: credit.credit_amount,
    credit_type: credit.credit_type,
    event_name: credit.event_name,
    event_date: credit.event_date,
    notes: credit.notes || ''
  })

  const typeColors: Record<string, string> = {
    production: 'bg-green-100 text-green-700',
    override: 'bg-blue-100 text-blue-700',
    bonus: 'bg-purple-100 text-purple-700',
    adjustment: 'bg-orange-100 text-orange-700'
  }

  const handleSave = () => {
    router.patch(`/equity_credits/${credit.id}`, { equity_credit: form }, {
      onSuccess: () => setIsEditing(false)
    })
  }

  return (
    <AppLayout>
      <Head title={`Equity Credit: ${credit.event_name}`} />

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/equity_credits" className="p-2 text-chestnut-charcoal/60 hover:text-chestnut-forest">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Equity Credit Details</h1>
              <p className="text-chestnut-charcoal/70 mt-1">{credit.event_name}</p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button onClick={() => setIsEditing(false)} className="btn-secondary flex items-center gap-2">
                    <X className="h-4 w-4" />
                    Cancel
                  </button>
                  <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                    <Save className="h-4 w-4" />
                    Save
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setIsEditing(true)} className="btn-secondary">
                    Edit
                  </button>
                  {canDelete && (
                    <button onClick={() => setShowDeleteModal(true)} className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg">
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card p-6 space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-chestnut-charcoal/10">
              <div className="p-3 bg-chestnut-gold/10 rounded-lg">
                <Award className="h-6 w-6 text-chestnut-gold" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-chestnut-charcoal">Credit Information</h2>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[credit.credit_type]}`}>
                  {credit.credit_type}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Agent</label>
                {isEditing ? (
                  <select
                    value={form.agent_id}
                    onChange={(e) => setForm({ ...form, agent_id: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                  >
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-chestnut-charcoal font-medium">
                    <Link href={`/agents/${credit.agent_id}`} className="text-chestnut-forest hover:underline">
                      {credit.agent_name}
                    </Link>
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Credit Type</label>
                {isEditing ? (
                  <select
                    value={form.credit_type}
                    onChange={(e) => setForm({ ...form, credit_type: e.target.value })}
                    className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                  >
                    <option value="production">Production</option>
                    <option value="override">Override</option>
                    <option value="bonus">Bonus</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                ) : (
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${typeColors[credit.credit_type]}`}>
                    {credit.credit_type}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Event Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={form.event_name}
                    onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                    className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                  />
                ) : (
                  <p className="text-chestnut-charcoal">{credit.event_name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Event Date</label>
                {isEditing ? (
                  <input
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                  />
                ) : (
                  <p className="text-chestnut-charcoal">{new Date(credit.event_date).toLocaleDateString()}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Notes</label>
              {isEditing ? (
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                />
              ) : (
                <p className="text-chestnut-charcoal">{credit.notes || 'No notes'}</p>
              )}
            </div>
          </div>

          <div className="card p-6 space-y-4">
            <h3 className="font-semibold text-chestnut-charcoal border-b border-chestnut-charcoal/10 pb-2">Credit Amount</h3>
            
            <div>
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Credits Granted</label>
              {isEditing ? (
                <input
                  type="number"
                  step="0.01"
                  value={form.credit_amount}
                  onChange={(e) => setForm({ ...form, credit_amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border border-chestnut-charcoal/20 rounded-lg"
                />
              ) : (
                <p className="text-3xl font-bold text-chestnut-forest">+{credit.credit_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              )}
            </div>

            <div className="pt-4 border-t border-chestnut-charcoal/10">
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Source</label>
              {credit.source_type === 'Commission' ? (
                <Link
                  href={`/commissions/${credit.source_id}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-chestnut-forest/5 border border-chestnut-forest/20 rounded-lg text-chestnut-forest hover:bg-chestnut-forest/10 hover:border-chestnut-forest/30 transition-colors group"
                >
                  <DollarSign className="h-4 w-4" />
                  <span className="font-medium group-hover:underline">View Commission #{credit.source_id}</span>
                  <span className="text-xs text-chestnut-charcoal/50">&rarr;</span>
                </Link>
              ) : (
                <p className="text-chestnut-charcoal">{credit.granted_by_name || 'Manual'}</p>
              )}
            </div>

            <div className="pt-4 border-t border-chestnut-charcoal/10">
              <label className="block text-sm font-medium text-chestnut-charcoal/70 mb-1">Created</label>
              <p className="text-sm text-chestnut-charcoal">{new Date(credit.created_at).toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Equity Credit"
        message="Are you sure you want to delete this equity credit? This will remove the credits from the agent's total."
        deleteUrl={`/equity_credits/${credit.id}`}
        itemName={`${credit.event_name} (+${credit.credit_amount} credits)`}
        redirectUrl="/equity_credits"
      />
    </AppLayout>
  )
}
